import { Controller } from "@hotwired/stimulus";
import "cally";

const CHEVRON_LEFT = '<svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M12.79 5.23a.75.75 0 010 1.06L8.06 11l4.73 4.71a.75.75 0 11-1.06 1.06l-5.25-5.25a.75.75 0 010-1.06l5.25-5.25a.75.75 0 011.06 0z" clip-rule="evenodd"/></svg>';
const CHEVRON_RIGHT = '<svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 010-1.06L11.94 9 7.21 4.29a.75.75 0 111.06-1.06l5.25 5.25a.75.75 0 010 1.06l-5.25 5.25a.75.75 0 01-1.06 0z" clip-rule="evenodd"/></svg>';

// How many years <calendar-select-year> lists at once, centered on
// whatever month is currently focused. With only "previous"/"next" month
// arrows, jumping to a date-of-birth decades back meant dozens of clicks
// (ZAP-12). 100 keeps the dropdown a single scroll for the common case
// (an adult's birth year) instead of paging by month; picking a year off
// the end of the list re-centers it, so nothing is ever truly
// unreachable, just a second pick away.
const YEAR_OPTIONS = 100;

let uid = 0;

// The display format everywhere in the app is DD/MM/YYYY - but Cally and
// Rails both work in ISO ("YYYY-MM-DD"), and Ruby's own date parser is
// genuinely ambiguous on plain DD/MM/YYYY (it guesses month-first for a
// slash-separated numeric date, so "03/04/2026" would silently become 4
// March, not 3 April). So ISO stays the real value everywhere except the
// text the visible input shows: #seedFromInput/#applyValue below convert
// on the way in and out, and the form-level "formdata" listener in
// #connect substitutes the real ISO value back in right before a form
// submits - the field's own name/submission never carries DD/MM/YYYY.
function isoToDisplay(iso) {
  const [ datePart, timePart ] = iso.split(" ");
  const [ year, month, day ] = datePart.split("-");
  return `${day}/${month}/${year}${timePart ? ` ${timePart}` : ""}`;
}

// Accepts either the display format above, or plain ISO - the latter is
// what the passport-scan flow writes directly into the field (see
// passport_scan_controller.js#fill, fed by PassportOcr::Result#as_json's
// plain `Date#as_json`), and what a server-rendered initial value used to
// be before this format existed. Returns null for anything else.
function displayToIso(raw) {
  if (/^\d{4}-\d{2}-\d{2}(?: \d{2}:\d{2})?$/.test(raw)) return raw;

  const match = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})(?: (\d{2}:\d{2}))?$/);
  if (!match) return null;

  const [ , day, month, year, time ] = match;
  return `${year}-${month}-${day}${time ? ` ${time}` : ""}`;
}

// Backs a plain, readonly text input with a Cally calendar (a scrollable
// 24h hour/minute wheel picker for datetime fields, Cally's own range mode
// for the payments filter) in place of the browser's native
// date/datetime-local widgets - their 12h/24h display and keyboard quirks
// come from the browser's own locale, not anything the app controls (the
// `lang` attribute doesn't force 24h in modern Chromium, despite older
// advice).
//
// Cally (<calendar-date>/<calendar-range>, see application.tailwind.css
// for its `::part()` theming) is deliberately headless - no popover, no
// positioning, no time picker - so all of that is built here: a native
// `[popover]` around the calendar, positioned with CSS anchor positioning
// (`.datepicker-popover`, same mechanism as `.marker-label`), plus a pair
// of scrollable hour/minute wheel columns (never a native time input -
// same locale problem as datetime-local) for datetime fields.
//
// The visible input shows "dd/mm/yyyy" (24h "dd/mm/yyyy HH:mm" for a
// datetime) - the one date format used everywhere in the app - while
// `this.currentIso` (see isoToDisplay/displayToIso above) tracks the real
// ISO value behind it; a "formdata" listener in #connect substitutes that
// back in right before the field's own form submits, since Rails/Ruby
// can't reliably parse a bare "dd/mm/yyyy" back into the right date. For
// a single date/datetime field, attach the controller directly to the
// input. A range picker (data-datepicker-range-value="true")
// instead needs a wrapping element with three targets - `input` (the
// visible, unsubmitted "start - end" display), `start` and `end` (the
// hidden fields actually submitted) - since picking a range writes into
// two fields and submits the enclosing form; see #onRangeChange below.
//
// The input can still be set programmatically from outside - e.g. the
// passport-scan flow filling in a date field (see
// passport_scan_controller.js#fill). That flow dispatches a plain `input`
// event after setting `.value`, which #onExternalInput below listens for
// to re-seed the calendar/time selects.
export default class extends Controller {
  static values = {
    time: { type: Boolean, default: false },
    range: { type: Boolean, default: false },
  };

  static targets = [ "input", "start", "end" ];

  connect() {
    this.input = this.hasInputTarget ? this.inputTarget : this.element;

    this.anchorName = `--datepicker-${++uid}`;
    this.input.style.anchorName = this.anchorName;

    this.buildPopover();
    this.seedFromInput();

    this.onOpen = () => this.popover.togglePopover();
    this.input.addEventListener("click", this.onOpen);

    this.onExternalInput = () => this.seedFromInput();
    this.input.addEventListener("input", this.onExternalInput);

    // Only the single (non-range) case needs this: `this.input` there IS
    // the submitted field, showing DD/MM/YYYY for display - "formdata"
    // fires right as the browser (or Turbo, which builds its request the
    // same way) is about to serialize the form, letting the real ISO
    // value stand in for that field without ever touching what's on
    // screen. The range picker already keeps display and submission
    // separate via its own start/end hidden fields, so `this.input.name`
    // there is blank and this is a no-op.
    this.form = this.input.closest("form");
    this.onFormData = (event) => {
      if (this.currentIso && this.input.name) event.formData.set(this.input.name, this.currentIso);
    };
    this.form?.addEventListener("formdata", this.onFormData);
  }

  disconnect() {
    this.input.removeEventListener("click", this.onOpen);
    this.input.removeEventListener("input", this.onExternalInput);
    this.form?.removeEventListener("formdata", this.onFormData);
    this.popover.remove();
  }

  buildPopover() {
    this.popover = document.createElement("div");
    this.popover.popover = "auto";
    this.popover.className = "datepicker-popover bg-base-100 border border-base-300 rounded-box shadow-lg p-3 flex-col gap-3 w-max";
    this.popover.style.positionAnchor = this.anchorName;

    this.calendar = document.createElement(this.rangeValue ? "calendar-range" : "calendar-date");
    this.calendar.classList.add("cally");
    this.calendar.innerHTML = `
      <span slot="previous" aria-hidden="true">${CHEVRON_LEFT}</span>
      <span slot="next" aria-hidden="true">${CHEVRON_RIGHT}</span>
      <calendar-select-month slot="heading"></calendar-select-month>
      <calendar-select-year slot="heading" max-years="${YEAR_OPTIONS}"></calendar-select-year>
      <calendar-month></calendar-month>
    `;
    this.calendar.addEventListener("change", () => this.onCalendarChange());

    // With a time picker, the calendar and the hour/minute wheels sit
    // side by side in a row rather than stacked - a `.datepicker-body`
    // wrapper holds both so the outer popover's own layout doesn't have
    // to change based on `timeValue`.
    const body = document.createElement("div");
    body.className = "datepicker-body";
    body.appendChild(this.calendar);
    this.popover.appendChild(body);

    if (this.timeValue) this.buildTimeRow(body);

    // A closed [popover] isn't rendered, so any scrollIntoView run while
    // it's closed (at connect/seed time) can't actually center a row -
    // there's no layout to scroll yet. Re-center both wheels once the
    // popover actually opens, when they do have layout.
    this.popover.addEventListener("toggle", (event) => {
      if (event.newState !== "open" || !this.timeValue) return;
      this.scrollRowIntoView(this.hourCol.rows[this.hour]);
      this.scrollRowIntoView(this.minuteCol.rows[this.minute]);
    });

    this.input.insertAdjacentElement("afterend", this.popover);
  }

  // A pair of scrollable "wheel" columns (hour 00-23, minute 00-59) in
  // place of native <select>s - operable by click, by scroll (CSS
  // scroll-snap centers a row, then a debounced scroll-settle listener
  // commits it), and by Up/Down arrow keys. `this.hour`/`this.minute`
  // are the source of truth; #applyValue reads them directly. See
  // application.tailwind.css for the `.time-picker*` styling.
  buildTimeRow(container) {
    this.hour = 0;
    this.minute = 0;

    this.hourCol = this.buildTimeColumn("hour", 24);
    this.minuteCol = this.buildTimeColumn("minute", 60);

    const sep = document.createElement("div");
    sep.className = "time-picker-sep";
    sep.setAttribute("aria-hidden", "true");
    sep.textContent = ":";

    const heading = document.createElement("div");
    heading.className = "time-picker-heading";
    heading.textContent = "Time";

    const row = document.createElement("div");
    row.className = "time-picker";
    row.setAttribute("role", "group");
    row.setAttribute("aria-label", "Time");
    row.append(this.hourCol, sep, this.minuteCol);

    const wrap = document.createElement("div");
    wrap.className = "time-picker-wrap";
    wrap.append(heading, row);
    container.appendChild(wrap);

    this.wireTimeColumn(this.hourCol, 24, (value) => { this.hour = value; this.applyValue(); });
    this.wireTimeColumn(this.minuteCol, 60, (value) => { this.minute = value; this.applyValue(); });

    // Match the old <select>'s implicit "00 selected" default so an
    // unseeded picker (new record, no existing value) doesn't open with
    // nothing highlighted and both columns scrolled to their raw top.
    this.hourCol.highlight(0);
    this.minuteCol.highlight(0);
    this.scrollRowIntoView(this.hourCol.rows[0]);
    this.scrollRowIntoView(this.minuteCol.rows[0]);
  }

  buildTimeColumn(unit, count) {
    const col = document.createElement("div");
    col.className = "time-picker-col";
    col.dataset.unit = unit;
    col.tabIndex = 0;
    col.setAttribute("role", "listbox");
    col.setAttribute("aria-label", unit === "hour" ? "Hour" : "Minute");

    for (let i = 0; i < count; i++) {
      const option = document.createElement("div");
      option.className = "time-picker-row";
      option.dataset.value = String(i);
      option.setAttribute("role", "option");
      option.textContent = String(i).padStart(2, "0");
      col.appendChild(option);
    }

    return col;
  }

  wireTimeColumn(col, count, onCommit) {
    const rows = Array.from(col.children);
    col.rows = rows;

    const highlight = (value) => {
      rows.forEach((row) => {
        const selected = Number(row.dataset.value) === value;
        row.classList.toggle("is-selected", selected);
        row.setAttribute("aria-selected", String(selected));
      });
    };
    col.highlight = highlight;

    const commit = (value, { scroll = true } = {}) => {
      highlight(value);
      if (scroll) this.scrollRowIntoView(rows[value]);
      onCommit(value);
    };
    col.commit = commit;

    col.addEventListener("click", (event) => {
      const row = event.target.closest(".time-picker-row");
      if (!row) return;
      commit(Number(row.dataset.value));
    });

    let debounceTimer = null;
    const onScrollSettled = () => {
      if (this.suppressScrollCommit) return;

      const mid = col.scrollTop + col.clientHeight / 2;
      let closest = rows[0];
      let closestDistance = Infinity;
      for (const row of rows) {
        const distance = Math.abs((row.offsetTop + row.offsetHeight / 2) - mid);
        if (distance < closestDistance) {
          closestDistance = distance;
          closest = row;
        }
      }
      commit(Number(closest.dataset.value), { scroll: false }); // already snapped there
    };

    if ("onscrollend" in window) {
      col.addEventListener("scrollend", onScrollSettled);
    } else {
      col.addEventListener("scroll", () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(onScrollSettled, 120);
      });
    }

    col.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
      event.preventDefault();

      const current = rows.findIndex((row) => row.classList.contains("is-selected"));
      const next = event.key === "ArrowDown"
        ? Math.min(current + 1, count - 1)
        : Math.max(current - 1, 0);
      commit(next);
    });
  }

  // Scrolls `row` to the vertical center of its column instantly (never
  // "smooth" - a jump-scroll on open/seed would look broken) and
  // suppresses the scroll-settle listener while the browser catches up,
  // so programmatic scrolling never fires a spurious commit/`change`.
  scrollRowIntoView(row) {
    this.suppressScrollCommit = true;
    row.scrollIntoView({ block: "center", behavior: "auto" });
    requestAnimationFrame(() => requestAnimationFrame(() => { this.suppressScrollCommit = false; }));
  }

  seedFromInput() {
    if (this.rangeValue) return; // range fields don't round-trip through `input`, see the view

    const raw = this.input.value.trim();
    if (!raw) return;

    const iso = displayToIso(raw);
    if (!iso) return;

    this.currentIso = iso;
    this.input.value = isoToDisplay(iso); // normalize - harmless if `raw` was already display-formatted

    const [ datePart, timePart ] = iso.split(" ");
    this.calendar.value = datePart;

    if (this.timeValue && timePart) {
      const [ hour, minute ] = timePart.split(":");
      this.hour = Number(hour) || 0;
      this.minute = Number(minute) || 0;

      // Bypass #commit so seeding never fires `change` - matching the
      // old <select>.value = ... assignment, which didn't fire it either.
      this.hourCol.highlight(this.hour);
      this.minuteCol.highlight(this.minute);
      this.scrollRowIntoView(this.hourCol.rows[this.hour]);
      this.scrollRowIntoView(this.minuteCol.rows[this.minute]);
    }
  }

  onCalendarChange() {
    if (this.rangeValue) {
      this.onRangeChange();
      return;
    }

    this.applyValue();
    if (!this.timeValue) this.popover.hidePopover();
  }

  applyValue() {
    const date = this.calendar.value;
    if (!date) return;

    let iso = date;
    if (this.timeValue) {
      const hour = String(this.hour).padStart(2, "0");
      const minute = String(this.minute).padStart(2, "0");
      iso += ` ${hour}:${minute}`;
    }

    this.currentIso = iso;
    this.input.value = isoToDisplay(iso);
    this.input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  async onRangeChange() {
    // The `change` event fires as part of Cally's click handling, but its
    // reactive `value` prop isn't guaranteed to have committed yet at that
    // exact point - `updated` resolves once it has.
    await this.calendar.updated;

    const [ start, end ] = this.calendar.value.split("/");
    if (this.hasStartTarget) this.startTarget.value = start || ""; // ISO - actually submitted, see the view
    if (this.hasEndTarget) this.endTarget.value = end || "";
    this.input.value = start && end ? `${isoToDisplay(start)} - ${isoToDisplay(end)}` : "";

    if (start && end) {
      this.popover.hidePopover();
      this.input.closest("form")?.requestSubmit();
    }
  }
}
